import { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, gql } from '@apollo/client';
import * as yup from 'yup';

import config from 'config';
import { makeRequired, requiredEmail } from 'lib/validators';
import { useAuth } from 'context/Auth';
import { useGlobal } from 'context/Global';
import Button from 'components/common/Button';
import Link from 'components/common/Link';
import FormTitle from 'components/form/FormTitle';
import FormFieldGroup from 'components/form/FormFieldGroup';
import FormNode from 'components/form/FormNode';
import FormLabeledInput from 'components/form/FormLabeledInput';
import FormNote from 'components/form/FormNote';

const CREATE_USER = gql`
  mutation CreateUser($email: String!, $password: String!, $firstName: String, $lastName: String) {
    createUser(
      User: { email: $email, password: $password, firstName: $firstName, lastName: $lastName }
    ) {
      id
    }
  }
`;

const errorMessages = {
  default: 'We were unable to create your user at this time. Please try again.',
  DuplicateEmail: 'An account already exists with that email address',
};

const validationSchema = yup.object().shape({
  firstName: makeRequired(yup.string()),
  lastName: makeRequired(yup.string()),
  email: requiredEmail,
  password: makeRequired(yup.string()),
});

const CreateAccountForm = ({ initialEmail, onSuccess }) => {
  const [createUser] = useMutation(CREATE_USER);
  const { addToast } = useGlobal();
  const { login, isLoggedIn } = useAuth();

  const { control, register, handleSubmit, formState } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: initialEmail || '',
      password: '',
    },
  });

  useEffect(() => {
    if (isLoggedIn) onSuccess();
  }, [isLoggedIn, onSuccess]);

  const { errors, isSubmitting } = formState;

  const onSubmit = useCallback(
    async (data) => {
      try {
        await createUser({ variables: data });
        await login({ email: data.email, password: data.password });
      } catch (err) {
        const errorType = err.message.replace('GraphQL error: ', '');
        const errorMessage = errorMessages[errorType];

        if (config('/debug') && !errorMessage) {
          console.error('No custom message for error:', errorType);
        }

        addToast({
          id: 'create-account',
          type: 'error',
          duration: 'long',
          message: errorMessage || errorMessages.default,
        });
      }
    },
    [createUser, login, addToast]
  );

  return (
    <div className="sm:mx-auto sm:max-w-md">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="py-6 sm:p-8 sm:bg-white sm:rounded-xl sm:shadow-xl"
      >
        <div className="mb-6">
          <FormTitle as="h1" className="text-2xl">
            Create Pledge It Account
          </FormTitle>
          {initialEmail && (
            <>
              <p className="mt-2 text-gray-700 truncate">
                for <strong className="font-medium">{initialEmail}</strong>
              </p>
              <input type="hidden" {...register('email')} />
            </>
          )}
        </div>
        <FormFieldGroup>
          <FormNode>
            <div>
              <Controller
                control={control}
                name="firstName"
                render={({ field, fieldState }) => (
                  <FormLabeledInput
                    type="text"
                    label="First name"
                    status={fieldState.error ? 'error' : 'default'}
                    {...field}
                  />
                )}
              />
            </div>
            <ErrorMessage
              errors={errors}
              name="firstName"
              render={({ message }) => <FormNote status="error">{message}</FormNote>}
            />
          </FormNode>
          <FormNode>
            <div>
              <Controller
                control={control}
                name="lastName"
                render={({ field, fieldState }) => (
                  <FormLabeledInput
                    type="text"
                    label="Last name"
                    status={fieldState.error ? 'error' : 'default'}
                    {...field}
                  />
                )}
              />
            </div>
            <ErrorMessage
              errors={errors}
              name="lastName"
              render={({ message }) => <FormNote status="error">{message}</FormNote>}
            />
          </FormNode>
          {!initialEmail && (
            <FormNode>
              <div>
                <Controller
                  control={control}
                  name="email"
                  render={({ field, fieldState }) => (
                    <FormLabeledInput
                      type="email"
                      label="Email address"
                      status={fieldState.error ? 'error' : 'default'}
                      {...field}
                    />
                  )}
                />
              </div>
              <ErrorMessage
                errors={errors}
                name="email"
                render={({ message }) => <FormNote status="error">{message}</FormNote>}
              />
            </FormNode>
          )}
          <FormNode>
            <div>
              <Controller
                control={control}
                name="password"
                render={({ field, fieldState }) => (
                  <FormLabeledInput
                    type="password"
                    label="Password"
                    status={fieldState.error ? 'error' : 'default'}
                    {...field}
                  />
                )}
              />
            </div>
            <ErrorMessage
              errors={errors}
              name="password"
              render={({ message }) => <FormNote status="error">{message}</FormNote>}
            />
          </FormNode>
          <p className="text-gray-600 sm:mx-1 leading-relaxed">
            By creating an account, you agree to our{' '}
            <Link
              href="/tos"
              className="text-gray-600 underline hover:text-gray-500 transition-colors duration-200"
            >
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              href="/privacy"
              className="text-gray-600 underline hover:text-gray-500 transition-colors duration-200"
            >
              Privacy Policy
            </Link>
          </p>
          <FormNode>
            <Button
              as="button"
              type="submit"
              color="green"
              className="font-medium w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Create account'}
            </Button>
          </FormNode>
        </FormFieldGroup>
      </form>
    </div>
  );
};

CreateAccountForm.propTypes = {
  initialEmail: PropTypes.string,
  onSuccess: PropTypes.func,
};

CreateAccountForm.defaultProps = {
  initialEmail: '',
  onSuccess: () => {},
};

export default CreateAccountForm;
